import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ChapterList from '@/components/ChapterList';
import AudioPlayer from '@/components/AudioPlayer';
import TranscriptOverlay from '@/components/TranscriptOverlay';
import TranslationToggle, { TranslationMode } from '@/components/TranslationToggle';
import { apiClient } from '@/services/api';
import { useAudioPlayer } from '@/hooks/useAudioPlayer';
import { Book, Chapter, Section, Category } from '@/types';
import { formatDuration } from '@/utils/format';

const TRANSLATION_MODE_KEY = 'wisdom:player:translationMode';

const BookView: React.FC = () => {
  const { bookId } = useParams<{ bookId: string }>();
  const navigate = useNavigate();

  const [book, setBook] = useState<Book | null>(null);
  const [category, setCategory] = useState<Category | null>(null);
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [sections, setSections] = useState<Record<string, Section[]>>({});
  const [currentChapter, setCurrentChapter] = useState<Chapter | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showTranscript, setShowTranscript] = useState(false);
  const [translationMode, setTranslationMode] = useState<TranslationMode>(
    () => (localStorage.getItem(TRANSLATION_MODE_KEY) as TranslationMode) || 'modern'
  );

  const handleTranslationModeChange = useCallback((mode: TranslationMode) => {
    setTranslationMode(mode);
    localStorage.setItem(TRANSLATION_MODE_KEY, mode);
  }, []);

  const hasAppliedDefaultRate = useRef(false);
  const translationModeRef = useRef(translationMode);
  translationModeRef.current = translationMode;

  // Refs to access state without stale closures
  const sectionsRef = useRef(sections);
  sectionsRef.current = sections;
  const chaptersRef = useRef(chapters);
  chaptersRef.current = chapters;
  const currentChapterRef = useRef(currentChapter);
  currentChapterRef.current = currentChapter;

  // Ref to audioPlayer.play — avoids stale closure since the callback is defined before audioPlayer
  const playRef = useRef<((section: Section, trackList: Section[], audioUrl: string) => void) | null>(null);

  // Filter sections based on translation mode
  const filterSectionsByTranslation = useCallback((allSections: Section[], mode: TranslationMode): Section[] => {
    if (mode === 'both') {
      // classic first, then modern
      const classic = allSections.filter(s => (s.translation || 'classic') === 'classic');
      const modern = allSections.filter(s => s.translation === 'modern');
      return [...classic, ...modern];
    }
    // Filter to only the selected translation
    const filtered = allSections.filter(s => (s.translation || 'classic') === mode);
    // If no sections match (e.g. book has no translation field), return all
    return filtered.length > 0 ? filtered : allSections;
  }, []);

  // Provide audio URL fetcher to the hook for next/prev/auto-advance
  const getAudioUrl = useCallback(async (section: Section) => {
    if (!section.audio_file_id) {
      return '';
    }
    return apiClient.getAudioUrl(section.audio_file_id);
  }, []);

  // Called when the last section of a chapter finishes — advance to next chapter
  const handleChapterEnded = useCallback(async () => {
    const chaps = chaptersRef.current;
    const current = currentChapterRef.current;
    if (!bookId || !current || chaps.length === 0) return;

    const currentIdx = chaps.findIndex((c) => c.id === current.id);
    if (currentIdx < 0 || currentIdx >= chaps.length - 1) return; // last chapter, done

    const nextChapter = chaps[currentIdx + 1];
    setCurrentChapter(nextChapter);

    // Load next chapter's sections (cached if already fetched)
    let nextSections = sectionsRef.current[nextChapter.id];
    if (!nextSections) {
      try {
        nextSections = await apiClient.getSections(bookId, nextChapter.id);
        setSections((prev) => ({ ...prev, [nextChapter.id]: nextSections! }));
        sectionsRef.current = { ...sectionsRef.current, [nextChapter.id]: nextSections };
      } catch (err) {
        console.error('Error loading next chapter sections:', err);
        return;
      }
    }

    const filtered = filterSectionsByTranslation(nextSections, translationModeRef.current);
    let playable = filtered.filter(s => !!s.audio_file_id);
    if (playable.length === 0) {
      playable = nextSections.filter(s => !!s.audio_file_id);
    }
    if (playable.length > 0) {
      try {
        const audioUrl = await apiClient.getAudioUrl(playable[0].audio_file_id);
        playRef.current?.(playable[0], playable, audioUrl);
      } catch (err) {
        console.error('Error playing next chapter:', err);
      }
    }
  }, [bookId, filterSectionsByTranslation]);

  const audioPlayer = useAudioPlayer(getAudioUrl, handleChapterEnded);
  playRef.current = audioPlayer.play;

  // Load book data
  useEffect(() => {
    const loadBookData = async () => {
      if (!bookId) {
        setError('Book not found');
        return;
      }

      try {
        const [bookData, chaptersData, categoriesData] = await Promise.all([
          apiClient.getBook(bookId),
          apiClient.getChapters(bookId),
          apiClient.getCategories(),
        ]);

        setBook(bookData);
        setChapters(chaptersData);

        const foundCategory = categoriesData.find((c) => c.id === bookData.category);
        setCategory(foundCategory || null);
      } catch (err) {
        console.error('Error loading book:', err);
        setError('Failed to load book');
      } finally {
        setIsLoading(false);
      }
    };

    loadBookData();
  }, [bookId]);

  // Load sections for a chapter (returns cached or freshly fetched)
  const loadSectionsForChapter = useCallback(
    async (chapter: Chapter): Promise<Section[]> => {
      if (!bookId) return [];

      // Return cached if available
      if (sectionsRef.current[chapter.id]) {
        return sectionsRef.current[chapter.id];
      }

      try {
        const sectionsData = await apiClient.getSections(bookId, chapter.id);
        setSections((prev) => ({
          ...prev,
          [chapter.id]: sectionsData,
        }));
        sectionsRef.current = { ...sectionsRef.current, [chapter.id]: sectionsData };
        return sectionsData;
      } catch (err) {
        console.error('Error loading sections:', err);
        return [];
      }
    },
    [bookId]
  );

  // Handle chapter selection — load sections, then play from section 1
  const handleChapterSelect = useCallback(
    async (chapter: Chapter) => {
      setCurrentChapter(chapter);
      const chapterSections = await loadSectionsForChapter(chapter);
      const filtered = filterSectionsByTranslation(chapterSections, translationMode);
      let playable = filtered.filter(s => !!s.audio_file_id);
      if (playable.length === 0) {
        playable = chapterSections.filter(s => !!s.audio_file_id);
      }
      if (playable.length > 0) {
        const firstSection = playable[0];
        try {
          // Apply per-book default playback rate on first play
          if (!hasAppliedDefaultRate.current && book?.default_playback_rate) {
            audioPlayer.setPlaybackRate(book.default_playback_rate);
            hasAppliedDefaultRate.current = true;
          }
          const audioUrl = await apiClient.getAudioUrl(firstSection.audio_file_id);
          audioPlayer.play(firstSection, playable, audioUrl);
        } catch (err) {
          console.error('Error loading audio:', err);
        }
      }
    },
    [loadSectionsForChapter, audioPlayer, book, translationMode, filterSectionsByTranslation]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-[var(--text-secondary)]">Loading book...</p>
      </div>
    );
  }

  if (error || !book) {
    return (
      <div className="text-center py-12">
        <p className="text-[var(--text-secondary)] mb-4">{error}</p>
        <Link to="/" className="text-[var(--accent-primary)] hover:text-[var(--accent-hover)]">
          Back to home
        </Link>
      </div>
    );
  }

  const categoryLink = category ? `/category/${category.id}` : '/';

  // Detect passage-based books (Beguine mystics, etc.)
  const isPassageBased = book.id === 'mirror_of_simple_souls' || 
    (book as any).chapter_detection === 'passage_based' ||
    book.id.includes('gospel');
  const chapterLabel = isPassageBased ? 'Passage' : 'Chapter';
  const chaptersLabel = isPassageBased ? 'Passages' : 'Chapters';

  return (
    <div className="max-w-5xl mx-auto px-8 py-12 space-y-8 pb-32">
      {/* Back button */}
      <button
        onClick={() => {
          audioPlayer.pause();
          navigate(-1);
        }}
        className="flex items-center gap-2 text-[var(--accent-primary)] hover:text-[var(--accent-hover)] transition-colors"
      >
        <span>←</span>
        Back
      </button>

      {/* Book header */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 items-start">
        {/* Cover */}
        <div className="sm:col-span-1">
          {book.cover_image_url ? (
            <img
              src={book.cover_image_url}
              alt={book.title}
              className="rounded-xl shadow-lg w-full"
            />
          ) : (
            <div className="w-full aspect-square rounded-xl bg-gradient-to-br from-[var(--accent-primary)] to-[var(--accent-secondary)] flex items-center justify-center">
              <span className="text-6xl">📖</span>
            </div>
          )}
        </div>

        {/* Info */}
        <div className="sm:col-span-2 space-y-6">
          <div>
            <Link
              to={categoryLink}
              className="text-sm text-[var(--accent-primary)] hover:text-[var(--accent-hover)] uppercase tracking-wider font-medium mb-2 inline-block"
            >
              {category?.name || 'Category'}
            </Link>
            <h1 className="text-4xl font-serif font-bold text-[var(--text-primary)] mb-2">
              {book.title}
            </h1>
            <p className="text-xl text-[var(--text-secondary)]">
              by {book.author}
            </p>
          </div>

          <p className="text-[var(--text-secondary)] leading-relaxed text-lg">
            {book.description}
          </p>

          {isPassageBased && (
            <details className="group border border-[var(--border-primary)] rounded-xl p-4 bg-[var(--background-secondary)] transition-all duration-300 hover:border-[var(--accent-primary)] cursor-pointer">
              <summary className="flex justify-between items-center font-serif font-semibold text-[var(--accent-primary)] list-none">
                <span>🔍 Geeking Out: Curatorial Notes & Citation Guide</span>
                <span className="text-xl transition-transform duration-300 group-open:rotate-180">▾</span>
              </summary>
              <div className="mt-4 pt-4 border-t border-[var(--border-primary)] text-sm text-[var(--text-secondary)] space-y-4 cursor-default leading-relaxed" onClick={(e) => e.stopPropagation()}>
                <p>
                  Welcome to the deep end! If you're wondering how the sections and parenthetical abbreviations are organized, here is the secret decoder ring:
                </p>
                
                <div className="space-y-2">
                  <h4 className="font-semibold text-[var(--text-primary)]">✨ Why is this "Passage-Based"?</h4>
                  <p>
                    Rather than a dry, cover-to-cover recording of the full academic manuscripts (which are often repetitive or contain administrative material), Charlene has curated a specialized collection of peak spiritual verses. These represent the absolute zenith of the author's mystical experiences and realizations.
                  </p>
                </div>

                <div className="space-y-2">
                  <h4 className="font-semibold text-[var(--text-primary)]">🏷️ Decoding the Citations (Parentheses)</h4>
                  <p>
                    The tags inside the parentheses represent the exact original chapter and division citations from historical manuscripts, allowing you to cross-reference or research the academic text:
                  </p>
                  <ul className="list-disc list-inside pl-2 space-y-1 text-xs">
                    {book.id === 'mirror_of_simple_souls' && (
                      <>
                        <li><strong>Ch. 21, Div. 3:</strong> Chapter 21, Division 3 of the original Middle English manuscript.</li>
                        <li><strong>Translator's Prologue:</strong> The prologue written by the 14th-century Middle English translator.</li>
                      </>
                    )}
                    {book.id === 'flowing_light_of_the_godhead' && (
                      <>
                        <li><strong>I.1:</strong> Book I, Chapter 1 of Mechthild's original seven-part manuscript.</li>
                        <li><strong>II.3:</strong> Book II, Chapter 3.</li>
                      </>
                    )}
                    {book.id.includes('gospel') && (
                      <>
                        <li><strong>Saying 1 / Section 1:</strong> The numbered verse/logion recovered from the Oxyrhynchus or Nag Hammadi codices.</li>
                      </>
                    )}
                    <li>Allows scholars and curious minds to find the exact historical source of each contemplative pearl.</li>
                  </ul>
                </div>

                <div className="space-y-2">
                  <h4 className="font-semibold text-[var(--text-primary)]">🔗 Deep Dive Hyperlinks</h4>
                  <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs">
                    {book.id === 'mirror_of_simple_souls' && (
                      <>
                        <a href="https://en.wikipedia.org/wiki/Marguerite_Porete" target="_blank" rel="noopener noreferrer" className="text-[var(--accent-primary)] hover:underline flex items-center gap-1">
                          👤 About Marguerite Porete ↗
                        </a>
                        <a href="https://en.wikipedia.org/wiki/The_Mirror_of_Simple_Souls" target="_blank" rel="noopener noreferrer" className="text-[var(--accent-primary)] hover:underline flex items-center gap-1">
                          📖 About The Mirror ↗
                        </a>
                      </>
                    )}
                    {book.id === 'flowing_light_of_the_godhead' && (
                      <>
                        <a href="https://en.wikipedia.org/wiki/Mechthild_of_Magdeburg" target="_blank" rel="noopener noreferrer" className="text-[var(--accent-primary)] hover:underline flex items-center gap-1">
                          👤 About Mechthild of Magdeburg ↗
                        </a>
                        <a href="https://en.wikipedia.org/wiki/The_Flowing_Light_of_the_Godhead" target="_blank" rel="noopener noreferrer" className="text-[var(--accent-primary)] hover:underline flex items-center gap-1">
                          📖 About The Flowing Light ↗
                        </a>
                      </>
                    )}
                    {book.id.includes('beguine') || book.id === 'mirror_of_simple_souls' || book.id === 'flowing_light_of_the_godhead' || book.id === 'hadewijch_visions_songs_letters' ? (
                      <a href="https://en.wikipedia.org/wiki/Beguines_and_Beghards" target="_blank" rel="noopener noreferrer" className="text-[var(--accent-primary)] hover:underline flex items-center gap-1">
                        🔥 About the Beguine Movement ↗
                      </a>
                    ) : null}
                    {book.id === 'gospel_of_thomas' && (
                      <>
                        <a href="https://en.wikipedia.org/wiki/Gospel_of_Thomas" target="_blank" rel="noopener noreferrer" className="text-[var(--accent-primary)] hover:underline flex items-center gap-1">
                          📜 About the Gospel of Thomas ↗
                        </a>
                        <a href="http://www.earlychristianwritings.com/thomas.html" target="_blank" rel="noopener noreferrer" className="text-[var(--accent-primary)] hover:underline flex items-center gap-1">
                          🏛️ Scholar Translations ↗
                        </a>
                      </>
                    )}
                    {book.id === 'gospel_of_mary' && (
                      <>
                        <a href="https://en.wikipedia.org/wiki/Gospel_of_Mary" target="_blank" rel="noopener noreferrer" className="text-[var(--accent-primary)] hover:underline flex items-center gap-1">
                          📜 About the Gospel of Mary ↗
                        </a>
                        <a href="http://www.earlychristianwritings.com/gospelmary.html" target="_blank" rel="noopener noreferrer" className="text-[var(--accent-primary)] hover:underline flex items-center gap-1">
                          🏛️ Scholar Translations ↗
                        </a>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </details>
          )}

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-[var(--text-secondary)]">Year</p>
              <p className="text-lg font-semibold text-[var(--text-primary)]">
                {book.year_published}
              </p>
            </div>
            <div>
              <p className="text-sm text-[var(--text-secondary)]">Duration</p>
              <p className="text-lg font-semibold text-[var(--text-primary)]">
                {formatDuration(book.total_duration_seconds)}
              </p>
            </div>
            <div>
              <p className="text-sm text-[var(--text-secondary)]">{chaptersLabel}</p>
              <p className="text-lg font-semibold text-[var(--text-primary)]">
                {book.chapter_count}
              </p>
            </div>
            <div>
              <p className="text-sm text-[var(--text-secondary)]">Word Count</p>
              <p className="text-lg font-semibold text-[var(--text-primary)]">
                {(book.word_count / 1000).toFixed(0)}k
              </p>
            </div>
          </div>

          {/* Translation mode toggle */}
          {book.translations && book.translations.length > 1 && (
            <div>
              <p className="text-sm text-[var(--text-secondary)] mb-2">Translation</p>
              <TranslationToggle
                mode={translationMode}
                onChange={handleTranslationModeChange}
                availableTranslations={book.translations}
              />
            </div>
          )}

          {/* CTA */}
          <button
            onClick={() => {
              if (chapters.length > 0) {
                handleChapterSelect(chapters[0]);
              }
            }}
            className="w-full sm:w-auto px-8 py-3 rounded-lg bg-[var(--accent-primary)] text-white font-semibold hover:bg-[var(--accent-hover)] transition-colors text-lg"
          >
            Start Listening
          </button>
        </div>
      </div>

      {/* Chapters */}
      <section className="mt-12">
        <h2 className="text-2xl font-serif font-semibold text-[var(--text-primary)] mb-6">
          {chaptersLabel}
        </h2>
        <ChapterList
          chapters={chapters}
          activeChapterId={currentChapter?.id}
          onChapterSelect={handleChapterSelect}
          chapterLabel={chapterLabel}
        />
      </section>

      {/* Audio Player */}
      {audioPlayer.currentSection && (
        <AudioPlayer
          chapter={currentChapter}
          bookTitle={book.title}
          translationLabel={
            book.translations && book.translations.length > 1
              ? ((audioPlayer.currentSection?.translation || 'classic') === 'modern' ? 'Modern' : 'Classic')
              : undefined
          }
          sectionText={audioPlayer.currentSection?.text || ''}
          showTranscript={showTranscript}
          onToggleTranscript={() => setShowTranscript((v) => !v)}
          isPlaying={audioPlayer.isPlaying}
          progress={audioPlayer.progress}
          duration={audioPlayer.duration}
          volume={audioPlayer.volume}
          playbackRate={audioPlayer.playbackRate}
          trackList={audioPlayer.trackList}
          currentTrackIndex={audioPlayer.currentTrackIndex}
          onPlay={audioPlayer.resume}
          onPause={audioPlayer.pause}
          onSeek={audioPlayer.seek}
          onVolumeChange={audioPlayer.setVolume}
          onPlaybackRateChange={audioPlayer.setPlaybackRate}
          onNext={audioPlayer.next}
          onPrevious={audioPlayer.previous}
          onSegmentClick={audioPlayer.jumpToTrack}
        />
      )}

      {/* Karaoke transcript overlay */}
      {audioPlayer.currentSection && showTranscript && (
        <TranscriptOverlay
          sectionText={audioPlayer.currentSection.text || ''}
          literalText={audioPlayer.currentSection.literal}
          progress={audioPlayer.progress}
          duration={audioPlayer.duration}
          isPlaying={audioPlayer.isPlaying}
          chapterTitle={currentChapter?.title || ''}
          sectionNum={audioPlayer.currentTrackIndex + 1}
          sectionCount={audioPlayer.trackList.length}
          onClose={() => setShowTranscript(false)}
        />
      )}
    </div>
  );
};

export default BookView;
